import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import ExpandableTable from "@/components/desktop/list/table/ExpandableTable";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomButton from "@/components/desktop/button/CustomButton";
import InventoryTakingBalancedBanner from "./components/InventoryTakingBalancedBanner";

const MAX_VISIBLE_CODES = 5;

const STATUS_CONFIG = {
	missing: { label: "Faltante", variant: "danger" },
	extra: { label: "Extra", variant: "warning" },
	found: { label: "Encontrado", variant: "success" },
};

const STATUS_FILTERS = [
	{ key: "missing", label: "Faltantes", variant: "danger" },
	{ key: "extra", label: "Extras", variant: "warning" },
	{ key: "found", label: "Encontrados", variant: "success" },
];

const InventoryTakingDetails = ({ results, isLoading }) => {
	const [activeStatuses, setActiveStatuses] = useState(new Set(["missing", "extra"]));
	const [codesModal, setCodesModal] = useState(null);

	const items = useMemo(() => results || [], [results]);

	const statusCounts = useMemo(() => {
		const counts = { missing: 0, extra: 0, found: 0 };
		items.forEach((item) => {
			if (counts[item.status] !== undefined) counts[item.status]++;
		});
		return counts;
	}, [items]);

	const tableRows = useMemo(() => {
		const groups = {};

		items.forEach((item) => {
			const productKey = item.product_name || "Sin producto";
			if (!groups[productKey]) {
				groups[productKey] = { productName: productKey, missing: [], extra: [], found: [] };
			}
			if (groups[productKey][item.status] !== undefined) {
				groups[productKey][item.status].push(item.inventory_code);
			}
		});

		const rows = [];
		Object.values(groups)
			.sort((groupA, groupB) => groupA.productName.localeCompare(groupB.productName))
			.forEach((group) => {
				STATUS_FILTERS.forEach(({ key }) => {
					if (activeStatuses.has(key) && group[key].length > 0) {
						rows.push({
							_rowKey: `${group.productName}-${key}`,
							product_name: group.productName,
							status: key,
							codes: group[key],
						});
					}
				});
			});

		return rows;
	}, [items, activeStatuses]);

	const toggleStatus = (statusKey) => {
		setActiveStatuses((prev) => {
			const next = new Set(prev);
			if (next.has(statusKey)) {
				if (next.size > 1) next.delete(statusKey);
			} else {
				next.add(statusKey);
			}
			return next;
		});
	};

	const isBalanced = items.length > 0 && statusCounts.missing === 0 && statusCounts.extra === 0;

	if (isLoading) {
		return <div style={{ padding: "1rem", textAlign: "center" }}>Cargando resultados...</div>;
	}

	if (isBalanced) {
		return <InventoryTakingBalancedBanner foundCount={statusCounts.found} />;
	}

	const columns = [
		{ key: "product_name", label: "Producto" },
		{
			key: "codes",
			label: "Códigos",
			render: (codes, row) => (
				<CodesList
					codes={codes}
					onShowAll={() =>
						setCodesModal({
							productName: row.product_name,
							status: row.status,
							codes,
						})
					}
				/>
			),
		},
		{
			key: "status",
			label: "Estado",
			align: "center",
			render: (value, row) => {
				const config = STATUS_CONFIG[value] || { label: value, variant: "neutral" };
				return (
					<CustomBadge variant={config.variant} size="sm">
						{config.label} ({row.codes.length})
					</CustomBadge>
				);
			},
		},
	];

	const totalFiltered = tableRows.reduce((sum, row) => sum + row.codes.length, 0);

	return (
		<div>
			<div style={{ display: "flex", justifyContent: "flex-end", padding: "0.5rem 0.75rem 0" }}>
				<div style={{ display: "flex", gap: "0.4rem" }}>
					{STATUS_FILTERS.map(({ key, label }) => {
						const isActive = activeStatuses.has(key);
						return (
							<CustomButton
								key={key}
								variant={isActive ? "primary" : "ghost"}
								size="sm"
								onClick={() => toggleStatus(key)}
								style={{
									borderColor: isActive ? undefined : "var(--color-border)",
									border: isActive ? undefined : "1px solid var(--color-border)",
								}}
							>
								{label} ({statusCounts[key]})
							</CustomButton>
						);
					})}
				</div>
			</div>
			<ExpandableTable
				title="Resultados"
				subtitle={`Total: ${totalFiltered} inventarios`}
				columns={columns}
				data={tableRows}
				rowKey="_rowKey"
				fullWidth={true}
			/>
			{codesModal && (
				<CodesModal productName={codesModal.productName} status={codesModal.status} codes={codesModal.codes} onClose={() => setCodesModal(null)} />
			)}
		</div>
	);
};

const CodesList = ({ codes, onShowAll }) => {
	const visible = codes.slice(0, MAX_VISIBLE_CODES);
	const remaining = codes.length - MAX_VISIBLE_CODES;

	return (
		<div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", alignItems: "center" }}>
			{visible.map((code) => (
				<CustomBadge key={code} variant="neutral" size="sm">
					{code}
				</CustomBadge>
			))}
			{remaining > 0 && (
				<CustomButton variant="ghost" size="sm" onClick={onShowAll} style={{ padding: "0.15rem 0.5rem", fontSize: "0.75rem" }}>
					+{remaining} más
				</CustomButton>
			)}
		</div>
	);
};

const CodesModal = ({ productName, status, codes, onClose }) => {
	const config = STATUS_CONFIG[status] || { label: status, variant: "neutral" };

	return createPortal(
		<div
			style={{
				position: "fixed",
				inset: 0,
				backgroundColor: "rgba(0,0,0,0.4)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 1050,
			}}
			onClick={onClose}
		>
			<div
				style={{
					background: "var(--color-bg, #fff)",
					borderRadius: "8px",
					padding: "1.25rem",
					maxWidth: "500px",
					width: "90%",
					maxHeight: "70vh",
					overflow: "auto",
				}}
				onClick={(event) => event.stopPropagation()}
			>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
					<div>
						<span style={{ fontWeight: 600, fontSize: "0.95rem" }}>{productName}</span>
						<span style={{ marginLeft: "0.5rem" }}>
							<CustomBadge variant={config.variant} size="sm">
								{config.label} ({codes.length})
							</CustomBadge>
						</span>
					</div>
					<button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: "0.25rem" }} aria-label="Cerrar">
						<X size={20} />
					</button>
				</div>
				<div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
					{codes.map((code) => (
						<CustomBadge key={code} variant="neutral" size="sm">
							{code}
						</CustomBadge>
					))}
				</div>
			</div>
		</div>,
		document.body,
	);
};

export default InventoryTakingDetails;
